import { useEffect, useRef, useState } from 'react';
import {
  CLIENT_DISPLAY_NAME,
  CLIENT_FEED_CONFIGS,
  type Client,
  type UpsertClient,
} from '@cer-autofill/shared';
import { apiErrorReason, fetchClients, saveClient } from '../api.js';

const blankClient = (): UpsertClient => ({
  clientId: '',
  name: '',
  shortName: '',
  abn: '',
  billingAddress: { line1: '', suburb: '', state: '', postcode: '', country: 'Australia' },
  serviceM8CompanyUuid: '',
  invoiceEmail: '',
  invoiceCcList: [],
  feedConfigId: 'generic',
  rateCardId: null,
  invoicingNotes: '',
  active: true,
});

export function ClientManagementScreen() {
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState<UpsertClient>(blankClient);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      setClients(await fetchClients());
    } catch (err) {
      setError(apiErrorReason(err));
    }
  }
  useEffect(() => {
    void load();
  }, []);
  function edit(client: Client) {
    const { createdAt: _createdAt, updatedAt: _updatedAt, ...value } = client;
    setForm(value);
    setEditing(true);
    setSaved(false);
    requestAnimationFrame(() =>
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await saveClient(form);
      setForm(blankClient());
      setEditing(false);
      setSaved(true);
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {error && <p className="error">{error}</p>}
      <section className="admin-grid admin-grid--clients">
        <div className="card" ref={editorRef}>
          <h3>{editing ? `Edit ${CLIENT_DISPLAY_NAME}` : `Add ${CLIENT_DISPLAY_NAME}`}</h3>
          <form className="client-form" onSubmit={(e) => void submit(e)}>
            <label>
              <span>Stable code</span>
              <input
                value={form.clientId}
                disabled={editing}
                pattern="[a-z0-9-]+"
                required
                onChange={(e) => setForm({ ...form, clientId: e.target.value.toLowerCase() })}
              />
            </label>
            <label>
              <span>Legal name</span>
              <input
                value={form.name}
                required
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label>
              <span>Short name</span>
              <input
                value={form.shortName}
                required
                onChange={(e) => setForm({ ...form, shortName: e.target.value })}
              />
            </label>
            <label>
              <span>ABN</span>
              <input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} />
            </label>
            <label>
              <span>Invoice email</span>
              <input
                type="email"
                value={form.invoiceEmail}
                onChange={(e) => setForm({ ...form, invoiceEmail: e.target.value })}
              />
            </label>
            <label>
              <span>Invoice CC (comma separated)</span>
              <input
                value={form.invoiceCcList.join(', ')}
                onChange={(e) =>
                  setForm({
                    ...form,
                    invoiceCcList: e.target.value
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
            <label>
              <span>ServiceM8 company UUID</span>
              <input
                value={form.serviceM8CompanyUuid}
                onChange={(e) => setForm({ ...form, serviceM8CompanyUuid: e.target.value })}
              />
            </label>
            <label>
              <span>Feed configuration</span>
              <select
                value={form.feedConfigId}
                onChange={(e) => setForm({ ...form, feedConfigId: e.target.value })}
              >
                {CLIENT_FEED_CONFIGS.map((feed) => (
                  <option key={feed.feedConfigId} value={feed.feedConfigId}>
                    {feed.label}
                  </option>
                ))}
              </select>
            </label>
            <fieldset>
              <legend>Billing address</legend>
              <input
                aria-label="Address line 1"
                placeholder="Address line 1"
                value={form.billingAddress.line1}
                onChange={(e) =>
                  setForm({
                    ...form,
                    billingAddress: { ...form.billingAddress, line1: e.target.value },
                  })
                }
              />
              <div className="client-form__address">
                <input
                  aria-label="Suburb"
                  placeholder="Suburb"
                  value={form.billingAddress.suburb}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      billingAddress: { ...form.billingAddress, suburb: e.target.value },
                    })
                  }
                />
                <input
                  aria-label="State"
                  placeholder="State"
                  value={form.billingAddress.state}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      billingAddress: { ...form.billingAddress, state: e.target.value },
                    })
                  }
                />
                <input
                  aria-label="Postcode"
                  placeholder="Postcode"
                  value={form.billingAddress.postcode}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      billingAddress: { ...form.billingAddress, postcode: e.target.value },
                    })
                  }
                />
              </div>
            </fieldset>
            <label className="client-form__wide">
              <span>Invoicing notes</span>
              <textarea
                value={form.invoicingNotes}
                onChange={(e) => setForm({ ...form, invoicingNotes: e.target.value })}
              />
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />{' '}
              Active
            </label>
            <div className="client-form__actions">
              <button className="primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save client'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setForm(blankClient());
                  }}
                >
                  Cancel
                </button>
              )}
              {saved && (
                <span className="save-confirmation" role="status">
                  Client saved
                </span>
              )}
            </div>
          </form>
        </div>
      </section>
      <div className="clients-table" role="table">
        <div className="clients-table__row clients-table__row--header" role="row">
          <div>Client</div>
          <div>Invoice contact</div>
          <div>Feed</div>
          <div>ServiceM8</div>
          <div>Status</div>
          <div></div>
        </div>
        {clients.map((client) => (
          <div
            className={`clients-table__row ${editing && form.clientId === client.clientId ? 'is-editing' : ''}`}
            role="row"
            key={client.clientId}
          >
            <div>
              <strong>{client.shortName}</strong>
              <small>{client.name}</small>
            </div>
            <div>{client.invoiceEmail || <span className="muted">Not set</span>}</div>
            <div>{client.feedConfigId}</div>
            <div>
              {client.serviceM8CompanyUuid ? 'Linked' : <span className="muted">Not linked</span>}
            </div>
            <div>
              <span className={`status-badge ${client.active ? 'status-badge--active' : ''}`}>
                {client.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <button type="button" onClick={() => edit(client)}>
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
